import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { getMentorRoles } from '../../slices/trainingSlice';
import { getPersons } from '../../slices/personsSlice';
import EmptyState from '../../components/EmptyState';
import { Header } from '../../components/Header';
import withPersonLookup from '../../components/HOC/withPersonLookup';
import HorizontalSection from '../../components/HorizontalSection';
import Icon from '../../components/Icon';
import LoadScreen from '../../components/LoadScreen';
import MainArea from '../../components/MainArea';
import Page from '../../components/Page';
import PersonWrapper from '../../components/PersonWrapper';
import RoleCard from '../../components/RoleCard';
import Text from '../../components/Text';
import Tooltip from '../../components/Tooltip';
import Input from '../../components/formElements/Input';
import localeLookup from '../../config/locale';
import {
  compareLocal,
  formatDate,
  searchString,
  sortBy,
} from '../../utils/helpers';
import { EMPTY_ID } from '../../constants';

const mapStateToProps = (state) => {
  const { training } = state;
  return {
    trainingTasks: training.trainingTasks || {},
    roles: training.roles,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getMentorRoles,
      getPersons,
    },
    dispatch
  );

class OtherPrograms extends Component {
  constructor() {
    super();
    this.state = {
      isLoading: true,
      filterString: '',
    };
  }

  componentDidMount() {
    const { getMentorRoles, getPersons } = this.props;
    Promise.all([getMentorRoles(), getPersons()]).then(() =>
      this.setState({ isLoading: false })
    );
  }

  getDateSubtitle = (menteeId, roleId) => {
    const { trainingTasks } = this.props;
    const mentee = trainingTasks[menteeId];
    const menteeRole = mentee.roles[roleId];
    const { mentorId, endDate, startDate } = menteeRole;
    if (mentorId === EMPTY_ID || !startDate) return null;
    return `${formatDate(startDate)} - ${
      endDate ? `${formatDate(endDate)}` : ''
    }`;
  };

  getSubtitle = ({ menteeId, roleId }) => {
    const { trainingTasks } = this.props;

    const mentee = trainingTasks[menteeId];
    if (mentee.roles[roleId].trainingTasks > 0) {
      return (
        <Text as="span" color="blue">
          {localeLookup('translations.You have')}{' '}
          {mentee.roles[roleId].trainingTasks}{' '}
          {mentee.roles[roleId].trainingTasks === 1
            ? localeLookup('translations.training task')
            : localeLookup('translations.training tasks')}
        </Text>
      );
    }
    return null;
  };

  onChangeFilterString = (e) => {
    this.setState({ filterString: e.target.value });
  };

  matchesMentee = (mentee) => {
    const { filterString } = this.state;
    const person = this.props.lookupPerson(mentee.id);

    return searchString(
      `${mentee.name} ${
        person?.employeeNumber || mentee.employeeNumber || ''
      } ${person?.email || mentee.email || ''}`,
      filterString
    );
  };

  renderRole = ({ role, mentee }) => {
    const hasMentor = role.mentor !== '';
    const route = `/training/other mentors' programs/${role.id}?menteeId=${mentee.id}`;
    return (
      <Link
        to={{
          pathname: route,
          state: {
            title: `${mentee.name} - ${role.name}`,
            backTitle: localeLookup("translations.Other mentors' programs"),
          },
        }}
        key={role.id}
      >
        <RoleCard
          competenceLevel={role.competenceLevel}
          title={role.name}
          clickable
          notifications={role.notifications}
          subtitle={this.getSubtitle({ menteeId: mentee.id, roleId: role.id })}
          subtitle2={
            hasMentor && (
              <PersonWrapper
                id={role.mentorId}
                render={({ name, color, tooltip }) => (
                  <Text as="span" color={color}>
                    <Tooltip tooltip={tooltip}>
                      <span>
                        {localeLookup('translations.Mentor')}: {name}
                      </span>
                    </Tooltip>
                  </Text>
                )}
              />
            )
          }
          subtitle3={this.getDateSubtitle(mentee.id, role.id)}
          completionPercentage={role.completionPercentage}
          donutColor={hasMentor ? 'green' : 'grey'}
          renderRight={
            <Icon
              className="role-card__arrow"
              color="grey"
              kind="chevron-right"
            />
          }
        />
      </Link>
    );
  };

  renderMenteeSection = (menteeId) => {
    const { trainingTasks, lookupPerson } = this.props;
    const mentee = trainingTasks[menteeId];
    const person = lookupPerson(menteeId);
    const titleTooltip = `${person.initials} ${
      person.employeeNumber ? `· ${person.employeeNumber}` : ''
    }`;
    return (
      <HorizontalSection
        key={menteeId}
        titleTooltip={titleTooltip}
        title={mentee.name}
        items={Object.values(mentee.roles)}
        renderItem={(role) => this.renderRole({ role, mentee })}
      />
    );
  };

  render() {
    const { trainingTasks } = this.props;
    const { filterString, isLoading } = this.state;
    const sortedMenteeIds = sortBy(
      Object.keys(trainingTasks).filter((menteeId) =>
        this.matchesMentee(trainingTasks[menteeId])
      ),
      [(a, b) => compareLocal(trainingTasks[a]?.name, trainingTasks[b]?.name)]
    );
    if (Object.keys(trainingTasks).length === 0 && isLoading)
      return <LoadScreen />;
    return (
      <Page>
        <div className="other-programs">
          <Header
            leftComponent={
              <Input
                className="other-programs__header-search"
                onChange={this.onChangeFilterString}
                placeholder={localeLookup('translations.Search for person')}
                size="small"
                value={filterString}
              />
            }
          />
          <MainArea defaultPadding backgroundColor="grey">
            {sortedMenteeIds.map(this.renderMenteeSection)}
            {Object.keys(trainingTasks).length === 0 && (
              <EmptyState
                fullHeight
                title={localeLookup('translations.No tasks here!')}
                body={localeLookup(
                  "translations.You have no tasks in other mentors' programs"
                )}
              />
            )}
          </MainArea>
        </div>
      </Page>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withPersonLookup(OtherPrograms));
